import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './AddFunds.css';

const AddFunds = () => {
  const { authToken } = useAuth();
  const [adminInfo, setAdminInfo] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMsg, setUploadMsg] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
  const headers = { Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    fetchAdminContact();
  }, []);

  const fetchAdminContact = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/users/admin-contact`, { headers });
      setAdminInfo(res.data.admin);
      setUserCountry(res.data.userCountry);
    } catch (err) {
      console.error('Error fetching admin contact:', err);
      setError('Could not load admin contact information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type) && !file.name.endsWith('.jpeg') && !file.name.endsWith('.jpg') && !file.name.endsWith('.png') && !file.name.endsWith('.pdf')) {
        setError('Only JPEG, PNG, or PDF files are allowed.');
        setSelectedFile(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }
    setUploadMsg('Proof of deposit submitted! The admin will review and credit your account shortly.');
    setSelectedFile(null);
    document.getElementById('file-input').value = '';
    setError('');
  };

  if (loading) {
    return (
      <div className="add-funds-container">
        <div className="add-funds-header">
          <h1>Add Funds</h1>
          <p>Contact your regional admin to deposit funds</p>
        </div>
        <div className="add-funds-loading">Loading admin information...</div>
      </div>
    );
  }

  return (
    <div className="add-funds-container">
      <div className="add-funds-header">
        <h1>Add Funds</h1>
        <p>Deposit by contacting your regional administrator</p>
      </div>

      {error && <div className="add-funds-error">{error}</div>}
      {uploadMsg && <div className="add-funds-success">{uploadMsg}</div>}

      {adminInfo && (
        <div className="add-funds-content">
          {/* Admin Contact Card */}
          <div className="admin-contact-card">
            <h2>Administrator Contact</h2>
            <div className="contact-details">
              <div className="contact-row">
                <span className="contact-label">Name:</span>
                <span className="contact-value">{adminInfo.name}</span>
              </div>
              <div className="contact-row">
                <span className="contact-label">Email:</span>
                <span className="contact-value">{adminInfo.email}</span>
              </div>
              {adminInfo.phone && (
                <div className="contact-row">
                  <span className="contact-label">Phone:</span>
                  <span className="contact-value">{adminInfo.phone}</span>
                </div>
              )}
              {adminInfo.whatsapp && (
                <div className="contact-row">
                  <span className="contact-label">WhatsApp:</span>
                  <span className="contact-value">{adminInfo.whatsapp}</span>
                </div>
              )}
              {adminInfo.mobileMoney && (
                <div className="contact-row">
                  <span className="contact-label">Mobile Money:</span>
                  <span className="contact-value">{adminInfo.mobileMoney}</span>
                </div>
              )}
              {adminInfo.gpay && (
                <div className="contact-row">
                  <span className="contact-label">GPay / UPI:</span>
                  <span className="contact-value">{adminInfo.gpay}</span>
                </div>
              )}
            </div>
          </div>

          {/* Bank Details Card */}
          <div className="admin-bank-card">
            <h2>Bank Account Details</h2>
            <div className="bank-details">
              <div className="bank-row">
                <span className="bank-label">Bank Name:</span>
                <span className="bank-value">{adminInfo.bankName}</span>
              </div>
              <div className="bank-row">
                <span className="bank-label">Account Holder:</span>
                <span className="bank-value">{adminInfo.bankHolder}</span>
              </div>
              <div className="bank-row">
                <span className="bank-label">Account Number:</span>
                <span className="bank-value">{adminInfo.bankAccount}</span>
              </div>
              {adminInfo.ifscCode && adminInfo.ifscCode !== 'N/A' && (
                <div className="bank-row">
                  <span className="bank-label">IFSC Code:</span>
                  <span className="bank-value">{adminInfo.ifscCode}</span>
                </div>
              )}
              {adminInfo.swiftCode && adminInfo.swiftCode !== 'N/A' && (
                <div className="bank-row">
                  <span className="bank-label">SWIFT Code:</span>
                  <span className="bank-value">{adminInfo.swiftCode}</span>
                </div>
              )}
            </div>
          </div>

          {/* Instructions Card */}
          <div className="instructions-card">
            <h2>How to Deposit</h2>
            <ol className="instructions-list">
              <li>Transfer the desired amount to the admin's bank account or mobile money.</li>
              <li>Take a screenshot or save the payment receipt as a JPEG, PNG, or PDF.</li>
              <li>Upload the proof of payment below.</li>
              <li>The admin will verify and credit your account within 24 hours.</li>
            </ol>
          </div>

          {/* File Upload Card */}
          <div className="upload-card">
            <h2>Upload Proof of Deposit</h2>
            <p className="upload-info">Upload a screenshot or PDF of your payment receipt.</p>
            <div className="file-upload-area">
              <input
                id="file-input"
                type="file"
                accept=".jpeg,.jpg,.png,.pdf"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="file-input" className="file-label">
                {selectedFile ? selectedFile.name : 'Choose File (JPEG, PNG, PDF)'}
              </label>
            </div>
            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={!selectedFile}
            >
              Submit Proof
            </button>
          </div>
        </div>
      )}

      {!adminInfo && !loading && (
        <div className="no-admin-msg">
          <p>No admin contact information is available for your region.</p>
          <p>Please contact support for assistance.</p>
        </div>
      )}
    </div>
  );
};

export default AddFunds;